import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';

const STEPS = [
  { id: 'add_team_members', title: 'Add Team Members', description: 'Invite team members to join your organisation' },
  { id: 'assign_managers', title: 'Assign Managers', description: 'Define reporting relationships between managers and recruiters' },
  { id: 'define_specialisations', title: 'Define Specialisations', description: 'Configure recruiter focus areas and expertise' },
  { id: 'review', title: 'Review & Complete', description: 'Review your configuration and complete setup' }
];

export default function SetupWizard({ onComplete, onCancel }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadWizardState();
  }, []);

  const loadWizardState = async () => {
    try {
      const state = await authService.getSetupWizardState();
      const stepIndex = STEPS.findIndex(s => s.id === state.current_step);
      if (stepIndex >= 0) {
        setCurrentStep(stepIndex);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to load wizard state:', err);
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleStepComplete = () => {
    if (currentStep < STEPS.length - 1) {
      handleNext();
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
          <p className="mt-2 text-gray-500">Loading setup wizard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    index < currentStep
                      ? 'bg-green-500 text-white'
                      : index === currentStep
                      ? 'bg-[#00C6AE] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {index < currentStep ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-sm font-medium ${index === currentStep ? 'text-[#00C6AE]' : index < currentStep ? 'text-green-600' : 'text-gray-500'}`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">{step.description}</p>
                </div>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`h-1 flex-1 mx-2 transition-colors ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Step Content */}
      <div className="mb-8">
        {currentStep === 0 && (
          <AddTeamMembersStep onComplete={handleStepComplete} onError={setError} />
        )}
        {currentStep === 1 && (
          <AssignManagersStep onComplete={handleStepComplete} onError={setError} />
        )}
        {currentStep === 2 && (
          <DefineSpecialisationsStep onComplete={handleStepComplete} onError={setError} />
        )}
        {currentStep === 3 && (
          <ReviewStep onComplete={onComplete} onError={setError} />
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          onClick={onCancel}
          className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <div className="flex gap-3">
          {currentStep > 0 && (
            <button
              onClick={handlePrevious}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Previous
            </button>
          )}
          {currentStep < STEPS.length - 1 && (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Skip for Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Step 4A: Add Team Members
function AddTeamMembersStep({ onComplete, onError }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: 'recruiter'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const data = await authService.getTeamMembers();
      setMembers(data);
      setLoading(false);
    } catch (err) {
      onError(err.message || 'Failed to load team members');
      setLoading(false);
    }
  };

  const handleDeleteMember = async (userId, userName) => {
    const confirmMessage = `Are you sure you want to delete ${userName}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await authService.deleteTeamMember(userId);
      await loadMembers();
      onError('');
    } catch (err) {
      onError(err.message || 'Failed to delete team member');
    }
  };

  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return 'Email address is required';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    
    // Additional validation: check for common typos
    const emailLower = email.toLowerCase();
    const domain = emailLower.split('@')[1];
    
    if (domain) {
      // Check for common typos in domain
      const typos = {
        'gmial.com': 'gmail.com',
        'gmai.com': 'gmail.com',
        'gmal.com': 'gmail.com',
        'yahooo.com': 'yahoo.com',
        'yaho.com': 'yahoo.com',
      };
      
      if (typos[domain]) {
        return `Did you mean ${email.split('@')[0]}@${typos[domain]}?`;
      }
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate email
    const emailError = validateEmail(formData.email);
    if (emailError) {
      onError(emailError);
      setSubmitting(false);
      return;
    }

    try {
      await authService.addTeamMember(formData);
      await loadMembers();
      setFormData({ full_name: '', email: '', role: 'recruiter' });
      onError('');
    } catch (err) {
      onError(err.message || 'Failed to add team member');
    } finally {
      setSubmitting(false);
    }
  };

  // This step reports errors through the onError PROP, which is what the wizard
  // shell renders (line 110). The local error state it used to keep was written
  // once, never read, and never displayed -- removed rather than left as a
  // second, invisible error channel.

  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Team Members</h3>
      <p className="text-gray-600 mb-6">
        Invite team members to join your organisation. They will receive an email with instructions to set up their password.
      </p>

      {/* Add Member Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
            >
              <option value="admin">Administrator</option>
              <option value="manager">Manager</option>
              <option value="recruiter">Recruiter</option>
            </select>
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="mt-4 px-4 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Adding...' : 'Add Member'}
        </button>
      </form>

      {/* Members List */}
      {loading ? (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#00C6AE]"></div>
        </div>
      ) : members.length > 0 ? (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 mb-2">Team Members ({members.length})</h4>
          {members.map((member) => (
            <div key={member.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{member.display_name}</p>
                <p className="text-sm text-gray-500">{member.email} • {member.role}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  member.invitation_status === 'invited' 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {member.invitation_status === 'invited' ? 'Invited' : 'Active'}
                </span>
                {member.role !== 'admin' && (
                  <button
                    onClick={() => handleDeleteMember(member.user_id, member.display_name)}
                    className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete user"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-4">No team members added yet.</p>
      )}

      {members.length > 0 && (
        <div className="mt-6">
          <button
            onClick={onComplete}
            className="px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] transition-colors"
          >
            Continue to Next Step
          </button>
        </div>
      )}
    </div>
  );
}

// Step 4B: Assign Managers
function AssignManagersStep({ onComplete, onError }) {
  const [recruiters, setRecruiters] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recruitersData, managersData] = await Promise.all([
        authService.getRecruiters(),
        authService.getManagers()
      ]);
      setRecruiters(recruitersData);
      setManagers(managersData);
      
      // Initialize assignments
      const initialAssignments = {};
      recruitersData.forEach(recruiter => {
        initialAssignments[recruiter.user_id] = recruiter.manager_user_id || '';
      });
      setAssignments(initialAssignments);
      
      setLoading(false);
    } catch (err) {
      onError(err.message || 'Failed to load data');
      setLoading(false);
    }
  };

  const handleAssignmentChange = (recruiterId, managerId) => {
    setAssignments({ ...assignments, [recruiterId]: managerId });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const promises = Object.entries(assignments)
        .filter(([_, managerId]) => managerId)
        .map(([recruiterId, managerId]) =>
          authService.assignManager(recruiterId, managerId)
        );
      
      await Promise.all(promises);
      await loadData();
      onError('');
      if (onComplete) onComplete();
    } catch (err) {
      onError(err.message || 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
        <p className="mt-2 text-gray-500">Loading...</p>
      </div>
    );
  }

  if (recruiters.length === 0) {
    return (
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Assign Managers</h3>
        <p className="text-gray-600 mb-4">
          No recruiters found. Please add recruiters in the previous step.
        </p>
      </div>
    );
  }

  if (managers.length === 0) {
    return (
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Assign Managers</h3>
        <p className="text-gray-600 mb-4">
          No managers found. Please add managers in the previous step.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Assign Managers to Recruiters</h3>
      <p className="text-gray-600 mb-6">
        Each recruiter must be assigned to one manager. These relationships support reporting, oversight, and performance visibility.
      </p>

      <div className="space-y-4">
        {recruiters.map((recruiter) => (
          <div key={recruiter.user_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">{recruiter.display_name}</p>
              <p className="text-sm text-gray-500">{recruiter.email}</p>
            </div>
            <select
              value={assignments[recruiter.user_id] || ''}
              onChange={(e) => handleAssignmentChange(recruiter.user_id, e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
            >
              <option value="">Select Manager</option>
              {managers.map((manager) => (
                <option key={manager.user_id} value={manager.user_id}>
                  {manager.display_name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button
          onClick={handleSave}
          disabled={saving || Object.values(assignments).every(v => !v)}
          className="px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Assignments'}
        </button>
      </div>
    </div>
  );
}

// Step 4C: Define Specialisations
//
// Exported (in addition to the default SetupWizard export) so tests can
// render this step directly. Reaching it through the wizard's own
// navigation depends on a chain of unrelated authService calls succeeding
// first, which is not the point of testing the catalogue autocomplete.
export function DefineSpecialisationsStep({ onComplete, onError }) {
  const [users, setUsers] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [roleFamilies, setRoleFamilies] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [specialisations, setSpecialisations] = useState({
    territories: [],
    sectors: [],
    role_families: []
  });
  const [newTerritory, setNewTerritory] = useState('');
  const [newSector, setNewSector] = useState('');
  const [newRoleFamily, setNewRoleFamily] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // The catalogue's vocabulary (Task 6): the real industries, functions and
  // countries a label's text can bind to. A desk-empty failure here is the
  // safe direction to fail -- the checkbox lists of already-created labels
  // still work, there just isn't an autocomplete steering new ones.
  const [vocab, setVocab] = useState({ industries: [], functions: [], countries: [] });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    authService.getScopeVocabulary().then(setVocab).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadUserSpecialisation();
    }
  }, [selectedUser]);

  // Territory is a country. Sector spans BOTH vocabularies, because a
  // tenant's sector labels split across company industries and candidate
  // disciplines -- offering only one of them would steer admins into labels
  // that half-work.
  const suggestionsFor = (kind, typed) => {
    const pool = kind === 'territory' ? vocab.countries : [...vocab.industries, ...vocab.functions];
    const q = typed.trim().toLowerCase();
    if (!q) return [];
    return pool.filter((v) => v.toLowerCase().includes(q)).slice(0, 8);
  };

  const loadData = async () => {
    try {
      const [usersData, territoriesData, sectorsData, roleFamiliesData] = await Promise.all([
        authService.getRecruitersAndManagers(),
        authService.getTerritories(),
        authService.getSectors(),
        authService.getRoleFamilies()
      ]);
      setUsers(usersData);
      setTerritories(territoriesData);
      setSectors(sectorsData);
      setRoleFamilies(roleFamiliesData);
      if (usersData.length > 0) {
        setSelectedUser(usersData[0].user_id);
      }
      setLoading(false);
    } catch (err) {
      onError(err.message || 'Failed to load data');
      setLoading(false);
    }
  };

  const loadUserSpecialisation = async () => {
    if (!selectedUser) return;
    try {
      const data = await authService.getRecruiterSpecialisation(selectedUser);
      setSpecialisations({
        territories: data.territories || [],
        sectors: data.sectors || [],
        role_families: data.role_families || []
      });
    } catch (err) {
      // If no specialisation exists, that's fine
      setSpecialisations({ territories: [], sectors: [], role_families: [] });
    }
  };

  // Helper function to split items by common delimiters (and, or, comma)
  const splitItems = (input) => {
    if (!input || !input.trim()) return [];
    
    // Split by common delimiters: "and", "or", comma
    // Handle both "Singapore and Thailand" and "IT, Engineering and Analytics"
    const text = input.trim();
    
    // First, try splitting by comma
    let parts = text.split(',').map(p => p.trim()).filter(p => p);
    
    // Then split each part by "and" or "or"
    const finalParts = [];
    for (const part of parts) {
      const andSplit = part.split(/\s+and\s+/i).map(p => p.trim()).filter(p => p);
      for (const andPart of andSplit) {
        const orSplit = andPart.split(/\s+or\s+/i).map(p => p.trim()).filter(p => p);
        finalParts.push(...orSplit);
      }
    }
    
    // Remove duplicates and empty strings
    return [...new Set(finalParts.filter(p => p.length > 0))];
  };

  const handleAddTerritory = async () => {
    if (!newTerritory.trim()) return;
    
    try {
      // Split the input into multiple items
      const itemsToAdd = splitItems(newTerritory);
      
      if (itemsToAdd.length === 0) {
        onError('Please enter a valid territory name');
        return;
      }
      
      // Create all territories
      const newTerritories = [];
      for (const item of itemsToAdd) {
        try {
          // Check if territory already exists
          const existing = territories.find(t => t.name.toLowerCase() === item.toLowerCase());
          if (existing) {
            // If it exists, add it to specialisations if not already there
            if (!specialisations.territories.includes(existing.name)) {
              setSpecialisations({
                ...specialisations,
                territories: [...specialisations.territories, existing.name]
              });
            }
          } else {
            // Create new territory
            const data = await authService.createTerritory(item);
            newTerritories.push(data);
            // Auto-check the checkbox for newly added item
            setSpecialisations({
              ...specialisations,
              territories: [...specialisations.territories, data.name]
            });
          }
        } catch (err) {
          // If creation fails, try to find existing one
          const existing = territories.find(t => t.name.toLowerCase() === item.toLowerCase());
          if (existing && !specialisations.territories.includes(existing.name)) {
            setSpecialisations({
              ...specialisations,
              territories: [...specialisations.territories, existing.name]
            });
          }
        }
      }
      
      if (newTerritories.length > 0) {
        setTerritories([...territories, ...newTerritories]);
      }
      setNewTerritory('');
    } catch (err) {
      onError(err.message || 'Failed to create territory');
    }
  };

  const handleAddSector = async () => {
    if (!newSector.trim()) return;
    
    try {
      // Split the input into multiple items
      const itemsToAdd = splitItems(newSector);
      
      if (itemsToAdd.length === 0) {
        onError('Please enter a valid sector name');
        return;
      }
      
      // Create all sectors
      const newSectors = [];
      for (const item of itemsToAdd) {
        try {
          // Check if sector already exists
          const existing = sectors.find(s => s.name.toLowerCase() === item.toLowerCase());
          if (existing) {
            // If it exists, add it to specialisations if not already there
            if (!specialisations.sectors.includes(existing.name)) {
              setSpecialisations({
                ...specialisations,
                sectors: [...specialisations.sectors, existing.name]
              });
            }
          } else {
            // Create new sector
            const data = await authService.createSector(item);
            newSectors.push(data);
            // Auto-check the checkbox for newly added item
            setSpecialisations({
              ...specialisations,
              sectors: [...specialisations.sectors, data.name]
            });
          }
        } catch (err) {
          // If creation fails, try to find existing one
          const existing = sectors.find(s => s.name.toLowerCase() === item.toLowerCase());
          if (existing && !specialisations.sectors.includes(existing.name)) {
            setSpecialisations({
              ...specialisations,
              sectors: [...specialisations.sectors, existing.name]
            });
          }
        }
      }
      
      if (newSectors.length > 0) {
        setSectors([...sectors, ...newSectors]);
      }
      setNewSector('');
    } catch (err) {
      onError(err.message || 'Failed to create sector');
    }
  };

  const handleAddRoleFamily = async () => {
    if (!newRoleFamily.trim()) return;
    
    try {
      // Split the input into multiple items
      const itemsToAdd = splitItems(newRoleFamily);
      
      if (itemsToAdd.length === 0) {
        onError('Please enter a valid role family name');
        return;
      }
      
      // Create all role families
      const newRoleFamilies = [];
      for (const item of itemsToAdd) {
        try {
          // Check if role family already exists
          const existing = roleFamilies.find(rf => rf.name.toLowerCase() === item.toLowerCase());
          if (existing) {
            // If it exists, add it to specialisations if not already there
            if (!specialisations.role_families.includes(existing.name)) {
              setSpecialisations({
                ...specialisations,
                role_families: [...specialisations.role_families, existing.name]
              });
            }
          } else {
            // Create new role family
            const data = await authService.createRoleFamily(item);
            newRoleFamilies.push(data);
            // Auto-check the checkbox for newly added item
            setSpecialisations({
              ...specialisations,
              role_families: [...specialisations.role_families, data.name]
            });
          }
        } catch (err) {
          // If creation fails, try to find existing one
          const existing = roleFamilies.find(rf => rf.name.toLowerCase() === item.toLowerCase());
          if (existing && !specialisations.role_families.includes(existing.name)) {
            setSpecialisations({
              ...specialisations,
              role_families: [...specialisations.role_families, existing.name]
            });
          }
        }
      }
      
      if (newRoleFamilies.length > 0) {
        setRoleFamilies([...roleFamilies, ...newRoleFamilies]);
      }
      setNewRoleFamily('');
    } catch (err) {
      onError(err.message || 'Failed to create role family');
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await authService.defineRecruiterSpecialisation(selectedUser, {
        territories: specialisations.territories,
        sectors: specialisations.sectors,
        role_families: specialisations.role_families
      });
      onError('');
      // Move to next user or complete
      const currentIndex = users.findIndex(u => u.user_id === selectedUser);
      if (currentIndex < users.length - 1) {
        setSelectedUser(users[currentIndex + 1].user_id);
      } else {
        if (onComplete) onComplete();
      }
    } catch (err) {
      onError(err.message || 'Failed to save specialisation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
        <p className="mt-2 text-gray-500">Loading...</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Define Specialisation Profiles</h3>
        <p className="text-gray-600 mb-4">
          No recruiters or managers found. Please add team members in the first step.
        </p>
      </div>
    );
  }

  const selectedUserData = users.find(u => u.user_id === selectedUser);

  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Define Specialisation Profiles</h3>
      <p className="text-gray-600 mb-6">
        For each recruiter and manager, define their recruitment focus areas. This information helps SignalCue route candidates and prioritize opportunities based on expertise.
      </p>

      {/* User Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Recruiter or Manager</label>
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
        >
          {users.map((user) => (
            <option key={user.user_id} value={user.user_id}>
              {user.display_name} ({user.email}) - {user.role}
            </option>
          ))}
        </select>
      </div>

      {selectedUserData && (
        <>
          {/* Territories */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Recruitment Territories</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newTerritory}
                onChange={(e) => setNewTerritory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTerritory())}
                placeholder="Add territory (e.g., Singapore, APAC)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddTerritory}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Add
              </button>
            </div>
            {suggestionsFor('territory', newTerritory).length > 0 && (
              <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden">
                {suggestionsFor('territory', newTerritory).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setNewTerritory(v)}
                    className="block w-full text-left px-3 py-1 text-sm hover:bg-gray-50"
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {territories.map((t) => (
                <label key={t.territory_id} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
                  <input
                    type="checkbox"
                    checked={specialisations.territories.includes(t.name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSpecialisations({
                          ...specialisations,
                          territories: [...specialisations.territories, t.name]
                        });
                      } else {
                        setSpecialisations({
                          ...specialisations,
                          territories: specialisations.territories.filter(ter => ter !== t.name)
                        });
                      }
                    }}
                  />
                  <span>{t.name}</span>
                  {/* Amber matches the weight the data-quality dashboard gives a
                      gap, because a label that binds to nothing is one. */}
                  {t.matches && (t.matches.companies + t.matches.people === 0 ? (
                    <span className="ml-2 text-xs text-amber-700">0 records matched</span>
                  ) : (
                    <span className="ml-2 text-xs text-gray-500">
                      {t.matches.companies} companies · {t.matches.people} people
                    </span>
                  ))}
                </label>
              ))}
            </div>
          </div>

          {/* Sectors */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry Sectors</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newSector}
                onChange={(e) => setNewSector(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSector())}
                placeholder="Add sector (e.g., Technology, Finance)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddSector}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Add
              </button>
            </div>
            {suggestionsFor('sector', newSector).length > 0 && (
              <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden">
                {suggestionsFor('sector', newSector).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setNewSector(v)}
                    className="block w-full text-left px-3 py-1 text-sm hover:bg-gray-50"
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {sectors.map((s) => (
                <label key={s.sector_id} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
                  <input
                    type="checkbox"
                    checked={specialisations.sectors.includes(s.name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSpecialisations({
                          ...specialisations,
                          sectors: [...specialisations.sectors, s.name]
                        });
                      } else {
                        setSpecialisations({
                          ...specialisations,
                          sectors: specialisations.sectors.filter(sec => sec !== s.name)
                        });
                      }
                    }}
                  />
                  <span>{s.name}</span>
                  {s.matches && (s.matches.companies + s.matches.people === 0 ? (
                    <span className="ml-2 text-xs text-amber-700">0 records matched</span>
                  ) : (
                    <span className="ml-2 text-xs text-gray-500">
                      {s.matches.companies} companies · {s.matches.people} people
                    </span>
                  ))}
                </label>
              ))}
            </div>
          </div>

          {/* Role Families */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Role Families / Job Types</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newRoleFamily}
                onChange={(e) => setNewRoleFamily(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRoleFamily())}
                placeholder="Add role family (e.g., Data, Sales, Engineering)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddRoleFamily}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {roleFamilies.map((rf) => (
                <label key={rf.role_family_id} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200">
                  <input
                    type="checkbox"
                    checked={specialisations.role_families.includes(rf.name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSpecialisations({
                          ...specialisations,
                          role_families: [...specialisations.role_families, rf.name]
                        });
                      } else {
                        setSpecialisations({
                          ...specialisations,
                          role_families: specialisations.role_families.filter(rf_name => rf_name !== rf.name)
                        });
                      }
                    }}
                  />
                  <span>{rf.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : selectedUser === users[users.length - 1].user_id ? 'Save & Continue' : 'Save & Next'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Step 4D: Review & Complete
function ReviewStep({ onComplete, onError }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    loadReview();
  }, []);

  const loadReview = async () => {
    try {
      const data = await authService.getSetupReview();
      setReview(data);
      setLoading(false);
    } catch (err) {
      onError(err.message || 'Failed to load review data');
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Are you sure you want to complete the setup? This will mark your organisation as ready.')) {
      return;
    }

    setCompleting(true);
    try {
      await authService.completeSetup();
      if (onComplete) onComplete();
    } catch (err) {
      onError(err.message || 'Failed to complete setup');
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
        <p className="mt-2 text-gray-500">Loading review...</p>
      </div>
    );
  }

  if (!review) {
    return <p className="text-gray-500">No review data available.</p>;
  }

  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Review & Complete Setup</h3>
      <p className="text-gray-600 mb-6">
        Review your configuration before completing the setup. Once completed, your organisation will be marked as ready.
      </p>

      {/* Summary */}
      <div className="space-y-4 mb-6">
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">User Summary</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Administrators</p>
              <p className="text-2xl font-semibold text-gray-900">{review.role_counts?.admin || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Managers</p>
              <p className="text-2xl font-semibold text-gray-900">{review.role_counts?.manager || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Recruiters</p>
              <p className="text-2xl font-semibold text-gray-900">{review.role_counts?.recruiter || 0}</p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Reporting Relationships</h4>
          <p className="text-lg font-semibold text-gray-900">{review.relationships_count}</p>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Recruiters with Specialisations</h4>
          <p className="text-lg font-semibold text-gray-900">{review.specialisations_count}</p>
        </div>
      </div>

      {/* Missing Configuration */}
      {review.missing_configuration && review.missing_configuration.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-medium text-yellow-900 mb-2">Missing Configuration</h4>
          <ul className="list-disc list-inside text-sm text-yellow-800">
            {review.missing_configuration.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Complete Button */}
      <div className="mt-6">
        <button
          onClick={handleComplete}
          disabled={completing || !review.is_complete}
          className="px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {completing ? 'Completing...' : 'Complete Setup'}
        </button>
        {!review.is_complete && (
          <p className="mt-2 text-sm text-gray-500">
            Please complete all required configuration before finishing setup.
          </p>
        )}
      </div>
    </div>
  );
}
